"use client"
import { apiSpecificOffers } from '@/apis/offers';
import { apiExpiredOffers } from '@/apis/user';
import { OffersOffer, PaginationType } from '@/services/dataTypes';
import React, { useEffect, useState } from 'react'
import { filterOfferBanners, getBannerDimensions, getBaseImageUrl } from '@/constants/hooks';
import dynamic from 'next/dynamic';

const Banner = dynamic(() => import('./Banner'), {
    ssr: false,
});

const OffersListView = dynamic(() => import('./OffersListView'), {
    ssr: false,
});

interface Props {
    initialOffers: OffersOffer[];
    companyId: string;
    merchantId: string;
    awaited_p_id: string;
    mer_slug_type: string;
    mer_slug: string;
    offerBanner: OffersOffer[];
    domain: string;
    merchantName: string;
    pagination: PaginationType;
    ads_campaign: boolean;
}

const LazyLoadingOffers = ({ initialOffers, companyId, merchantId, awaited_p_id, mer_slug_type, mer_slug, offerBanner, domain, merchantName, pagination, ads_campaign }: Props) => {
    const [offers, setOffers] = useState<OffersOffer[]>(initialOffers);
    const [expiredOffers, setExpiredOffers] = useState<OffersOffer[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(pagination?.current_page || 1);
    const [hasMore, setHasMore] = useState((pagination?.current_page || 1) < (pagination?.last_page || 1));
    const [filteredOfferBanners, setFilteredOfferBanners] = useState<OffersOffer[]>([]);
    const [lazy, setLazy] = useState(false);
    const bannerDisplayAfter = 2;
    useEffect(() => {
        const timer = setTimeout(() => setLazy(true), 400);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        setOffers(initialOffers);
        setPage(pagination?.current_page || 1);
        setHasMore((pagination?.current_page || 1) < (pagination?.last_page || 1));
        fetchExpiredOffers();
    }, [initialOffers, pagination]);

    const loadMore = async () => {
        if (loading || !hasMore) return;
        if (page == pagination?.last_page) {
            setHasMore(false);
            return;
        }
        setLoading(true);
        try {
            const nextPage = page + 1;
            const response = await apiSpecificOffers(merchantId, companyId, nextPage);

            if (response.data?.offers.length > 0) {
                setOffers(prev => [...prev, ...response.data.offers]);
                setPage(nextPage);
                setHasMore(nextPage < (response.data?.pagination?.last_page || pagination?.last_page || nextPage));
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error('Error loading more offers:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchExpiredOffers = async () => {
        try {
            const response = await apiExpiredOffers(merchantId, companyId);
            setExpiredOffers(response.data?.offers || []);
        } catch (error) {
            // console.error("Error fetching expired offers:", error);
        }
    };

    useEffect(() => {
        if (offerBanner?.length > 0) {
            setFilteredOfferBanners(filterOfferBanners(offerBanner, 300, 600, 50, 150))
        }
    }, [offerBanner])

    return (
        <>
            {/* ── Active offers grid ── */}
            <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">
                {offers.map((offer, index) => (
                    offer.status === 1 && offer.offer.status === 1 ? (
                        <React.Fragment key={offer.id}>
                            <div>
                                <OffersListView
                                    product={offer}
                                    companyId={companyId}
                                    awaited_p_id={awaited_p_id}
                                    mer_slug_type={mer_slug_type}
                                    mer_slug={mer_slug}
                                    domain={domain}
                                    ads_campaign={ads_campaign}
                                />
                            </div>
                            {(index + 1) % bannerDisplayAfter === 0 &&
                                filteredOfferBanners.length > 0 &&
                                filteredOfferBanners[Math.floor(index / bannerDisplayAfter)] && (() => {
                                    const banner = filteredOfferBanners[Math.floor(index / bannerDisplayAfter)];
                                    const dimension = getBannerDimensions(banner);
                                    return (
                                        <div className="sm:col-span-2 w-full flex justify-center py-2" key={`banner-${Math.floor(index / bannerDisplayAfter)}`}>
                                            <Banner
                                                data={banner} height={dimension?.height} width={dimension?.width} offerLink={null} domain={domain} mer_slug={mer_slug} slug_type={mer_slug_type}
                                            />
                                        </div>
                                    );
                                })()
                            }
                        </React.Fragment>
                    ) : null
                ))}

                {/* Loading spinner */}
                {loading && (
                    <div className="sm:col-span-2 flex justify-center items-center py-8">
                        <div className="w-8 h-8 rounded-full border-2 border-gray-100 border-t-[#f23017] animate-spin" />
                    </div>
                )}
            </div>

            {/* ── Expired offers ── */}
            {hasMore && (
                <div className="mt-6 flex justify-center">
                    <button
                        type="button"
                        onClick={loadMore}
                        disabled={loading}
                        className="inline-flex items-center justify-center rounded-xl border border-[#f23017]/25 bg-white px-6 py-3 text-xs font-black uppercase tracking-wider text-[#f23017] shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-[#f23017] hover:bg-[#f23017] hover:text-white hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {loading ? 'Loading Deals...' : 'Show More Deals'}
                    </button>
                </div>
            )}

            {expiredOffers?.length > 0 && (
                <div className="mt-10">
                    <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200">
                        <div className="w-1 h-6 bg-gray-300 rounded-full shrink-0" />
                        <h2 className="text-[15px] font-black text-gray-400 m-0">
                            Expired Deals — May Still Work for{' '}
                            <span className="text-[#f23017]">{merchantName}</span>
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 opacity-60">
                        {expiredOffers.slice(0, 10).map((item, i) => (
                            <OffersListView
                                key={i}
                                product={item}
                                companyId={companyId}
                                awaited_p_id={awaited_p_id}
                                mer_slug_type={mer_slug_type}
                                mer_slug={mer_slug}
                                domain={domain}
                                ads_campaign={ads_campaign}
                            />
                        ))}
                    </div>
                </div>
            )}

            <script
                type="application/ld+json"
                id='AggregateOffer'
                dangerouslySetInnerHTML={{
                    "__html": JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "AggregateOffer",
                        "name": `${merchantName} Coupons & Deals`,
                        "description": `Currently, there are several active coupons and deals available for ${merchantName} products.`,
                        "offerCount": offers.length,
                        "offers": offers.map((offer) => ({
                            "@type": "Offer",
                            "name": offer?.offer?.offer_title,
                            "description": offer?.offer?.offer_detail,
                            "url": getBaseImageUrl(`${domain}`, `${offer.offer?.url}`, ""),
                        }))
                    })
                }}
            />
        </>
    )
}

export default LazyLoadingOffers

